// src/Dashboard/pages/admin/Attendance/hooks/useAttendance.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { getGroupsList } from "../../../../../services/groupServices";
import { getAllAdherent } from "../../../../../services/adherentServices";
import { getSettings } from "../../../../../services/settingsServices";

import {
  getAllAttendances,
  getAttendancesByGroupAndDate,
  countAttendance,
  setAttendance as apiSetAttendance,
  clearAttendance as apiClearAttendance,
  deleteAttendancesByPeriod,
} from "../../../../../services/attendanceServices";

import {
  STATUS_OPTIONS,
  adherentBelongsToGroup,
  buildAttendanceRows,
  buildDailyAttendanceMap,
  buildMonthlyChartData,
  generateSeasonMonths,
  getAdherentGroupLabel,
  getChartTotals,
  getCurrentSeasonMonth,
  getLocalDateInput,
  getSeasonNeighbors,
  hasStoppedStatus,
  normalizeAttendanceFilters,
  sortAdherents,
} from "../utils/attendanceUtils";

const DEFAULT_FILTERS = {
  query: "",
  selectedGroupId: "",
  selectedDate: getLocalDateInput(),
  selectedSeason: "",
  statutInscription: "",
  hideStopped: true,
  onlyPresent: false,
  onlyAbsent: false,
  sortByName: "name_asc",
};

function isActiveGroup(group) {
  return (
    group?.is_active === true ||
    group?.is_active === 1 ||
    group?.is_active === "1" ||
    group?.is_active === "true" ||
    group?.is_active == null
  );
}

export default function useAttendance() {
  const [activePanel, setActivePanel] = useState("call");

  const [loading, setLoading] = useState(true);
  const [loadingAttendances, setLoadingAttendances] = useState(false);

  const [groups, setGroups] = useState([]);
  const [adherents, setAdherents] = useState([]);
  const [seasons, setSeasons] = useState([]);

  const [filtersState, setFiltersState] = useState(DEFAULT_FILTERS);

  const [attendance, setAttendance] = useState({});
  const attendanceRef = useRef(attendance);

  const [dailyAttendanceCache, setDailyAttendanceCache] = useState([]);
  const [allAttendances, setAllAttendances] = useState(null);

  const [savingIds, setSavingIds] = useState(new Set());
  const [assiduityInfo, setAssiduityInfo] = useState({});

  const [selectedMonth, setSelectedMonth] = useState("");
  const [seasonActive, setSeasonActive] = useState("");

  const [deleteForm, setDeleteForm] = useState({
    startDate: getLocalDateInput(),
    endDate: getLocalDateInput(),
  });

  const [deleteResult, setDeleteResult] = useState(null);

  const filters = useMemo(
    () => normalizeAttendanceFilters(filtersState),
    [filtersState]
  );

  const setFilter = useCallback((key, value) => {
    setFiltersState((previous) =>
      normalizeAttendanceFilters({
        ...previous,
        [key]: value,
      })
    );
  }, []);

  const selectedGroup = useMemo(() => {
    return groups.find(
      (group) => String(group.id) === String(filters.selectedGroupId)
    );
  }, [groups, filters.selectedGroupId]);

  const loadBaseData = useCallback(async () => {
    try {
      setLoading(true);

      const [settingsData, groupList, adherentList] = await Promise.all([
        getSettings(),
        getGroupsList(),
        getAllAdherent(),
      ]);

      const settings = Array.isArray(settingsData)
        ? settingsData[0]
        : settingsData;

      const safeGroups = Array.isArray(groupList) ? groupList : [];
      const safeAdherents = Array.isArray(adherentList) ? adherentList : [];

      const activeGroups = safeGroups
        .filter(isActiveGroup)
        .slice()
        .sort((a, b) => {
          const orderA = Number(a?.ordre_groupe || 9999);
          const orderB = Number(b?.ordre_groupe || 9999);

          if (orderA !== orderB) return orderA - orderB;

          return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr", {
            sensitivity: "base",
          });
        });

      const activeSeason = settings?.saison_active || "";
      const seasonMonths = generateSeasonMonths(activeSeason);
      const currentMonth =
        getCurrentSeasonMonth(seasonMonths) || seasonMonths?.[0]?.value || "";

      setGroups(activeGroups);
      setAdherents(safeAdherents);
      setSeasonActive(activeSeason);
      setSelectedMonth((previous) => previous || currentMonth);

      if (activeSeason) {
        const { prev, next } = getSeasonNeighbors(activeSeason);
        setSeasons([prev, activeSeason, next].filter(Boolean));
      }

      setFiltersState((previous) =>
        normalizeAttendanceFilters({
          ...previous,
          selectedSeason: previous.selectedSeason || activeSeason,
          selectedDate: previous.selectedDate || getLocalDateInput(),
        })
      );
    } catch (error) {
      console.error("Erreur chargement données appel :", error);
      toast.error("Erreur lors du chargement de la page d'appel.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBaseData();
  }, [loadBaseData]);

  const groupAdherents = useMemo(() => {
    let list = (adherents || []).filter((adherent) => {
      const matchesGroup = adherentBelongsToGroup(adherent, selectedGroup);

      const matchesSeason =
        !filters.selectedSeason ||
        adherent?.saison_active === filters.selectedSeason;

      const matchesStatus =
        !filters.statutInscription ||
        adherent?.statut_inscription === filters.statutInscription;

      const matchesStopped =
        !filters.hideStopped || !hasStoppedStatus(adherent);

      return matchesGroup && matchesSeason && matchesStatus && matchesStopped;
    });

    return sortAdherents(list, filters.sortByName);
  }, [adherents, filters, selectedGroup]);

  const loadGroupAttendance = useCallback(async () => {
    if (!filters.selectedGroupId || !filters.selectedDate || !selectedGroup) {
      setAttendance({});
      setDailyAttendanceCache([]);
      attendanceRef.current = {};
      return;
    }

    try {
      setLoadingAttendances(true);

      const attendanceList = await getAttendancesByGroupAndDate(
        filters.selectedGroupId,
        filters.selectedDate
      );

      const safeAttendances = Array.isArray(attendanceList)
        ? attendanceList
        : [];

      setDailyAttendanceCache(safeAttendances);

      const selectedAdherentIds = groupAdherents.map((adherent) => adherent.id);

      const map = buildDailyAttendanceMap({
        dateISO: filters.selectedDate,
        groupId: filters.selectedGroupId,
        adherentIds: selectedAdherentIds,
        attendances: safeAttendances,
      });

      setAttendance(map);
      attendanceRef.current = map;
    } catch (error) {
      console.error("Erreur chargement appel groupe/date :", error);
      toast.error("Erreur lors du chargement des présences du groupe.");
    } finally {
      setLoadingAttendances(false);
    }
  }, [
    filters.selectedGroupId,
    filters.selectedDate,
    selectedGroup,
    groupAdherents,
  ]);

  useEffect(() => {
    loadGroupAttendance();
  }, [loadGroupAttendance]);

  useEffect(() => {
    attendanceRef.current = attendance;
  }, [attendance]);

  const ensureAllAttendancesLoaded = useCallback(async () => {
    if (Array.isArray(allAttendances)) return allAttendances;

    try {
      setLoadingAttendances(true);

      const data = await getAllAttendances();
      const safeData = Array.isArray(data) ? data : [];

      setAllAttendances(safeData);

      return safeData;
    } catch (error) {
      console.error("Erreur chargement toutes présences :", error);
      toast.error("Erreur lors du chargement des appels.");
      return [];
    } finally {
      setLoadingAttendances(false);
    }
  }, [allAttendances]);

  useEffect(() => {
    if (activePanel === "table" || activePanel === "charts") {
      ensureAllAttendancesLoaded();
    }
  }, [activePanel, ensureAllAttendancesLoaded]);

  const filteredAdherents = useMemo(() => {
    const query = String(filters.query || "").trim().toLowerCase();

    let list = groupAdherents.filter((adherent) => {
      const attendanceStatus = attendance?.[adherent.id] ?? 0;

      const matchesPresent = !filters.onlyPresent || attendanceStatus === 1;
      const matchesAbsent = !filters.onlyAbsent || attendanceStatus === 2;

      const groupLabel = getAdherentGroupLabel(adherent, groups);

      const matchesQuery =
        !query ||
        [
          adherent?.nom,
          adherent?.prenom,
          adherent?.email,
          adherent?.licence_number,
          adherent?.statut_inscription,
          groupLabel,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

      return matchesPresent && matchesAbsent && matchesQuery;
    });

    return sortAdherents(list, filters.sortByName);
  }, [groupAdherents, filters, attendance, groups]);

  const setSavingForId = useCallback((adherentId, value) => {
    setSavingIds((previous) => {
      const next = new Set(previous);

      if (value) next.add(String(adherentId));
      else next.delete(String(adherentId));

      return next;
    });
  }, []);

  const updateAttendancesCache = useCallback((updater) => {
    setAllAttendances((previous) => {
      if (!Array.isArray(previous)) return previous;
      return updater(previous);
    });
  }, []);

  const toggleAttendance = useCallback(
    async (adherentId) => {
      if (
        loading ||
        loadingAttendances ||
        !filters.selectedGroupId ||
        !filters.selectedDate ||
        savingIds.has(String(adherentId))
      ) {
        return;
      }

      const previousValue = attendanceRef.current[adherentId] ?? 0;
      const nextValue = previousValue === 0 ? 1 : previousValue === 1 ? 2 : 0;

      const groupId = filters.selectedGroupId;
      const dateISO = String(filters.selectedDate).slice(0, 10);

      setAttendance((previous) => {
        const next = {
          ...previous,
          [adherentId]: nextValue,
        };

        attendanceRef.current = next;

        return next;
      });

      const matchesCell = (item) =>
        Number(item.adherent_id) === Number(adherentId) &&
        Number(item.group_id) === Number(groupId) &&
        String(item.date_presence).slice(0, 10) === dateISO;

      try {
        setSavingForId(adherentId, true);

        if (nextValue === 1) {
          await apiSetAttendance(adherentId, groupId, dateISO, true);

          setDailyAttendanceCache((previous) => [
            ...(previous || []).filter((item) => !matchesCell(item)),
            {
              adherent_id: Number(adherentId),
              group_id: Number(groupId),
              presence: true,
              date_presence: dateISO,
            },
          ]);

          updateAttendancesCache((previous) => [
            ...previous.filter((item) => !matchesCell(item)),
            {
              adherent_id: Number(adherentId),
              group_id: Number(groupId),
              presence: true,
              date_presence: dateISO,
            },
          ]);
        } else if (nextValue === 2) {
          await apiSetAttendance(adherentId, groupId, dateISO, false);

          setDailyAttendanceCache((previous) => [
            ...(previous || []).filter((item) => !matchesCell(item)),
            {
              adherent_id: Number(adherentId),
              group_id: Number(groupId),
              presence: false,
              date_presence: dateISO,
            },
          ]);

          updateAttendancesCache((previous) => [
            ...previous.filter((item) => !matchesCell(item)),
            {
              adherent_id: Number(adherentId),
              group_id: Number(groupId),
              presence: false,
              date_presence: dateISO,
            },
          ]);
        } else {
          await apiClearAttendance(adherentId, groupId, dateISO);

          setDailyAttendanceCache((previous) =>
            (previous || []).filter((item) => !matchesCell(item))
          );

          updateAttendancesCache((previous) =>
            previous.filter((item) => !matchesCell(item))
          );
        }
      } catch (error) {
        console.error("Erreur mise à jour présence :", error);

        setAttendance((previous) => {
          const next = {
            ...previous,
            [adherentId]: previousValue,
          };

          attendanceRef.current = next;

          return next;
        });

        toast.error("Impossible d'enregistrer cette présence.");
      } finally {
        setSavingForId(adherentId, false);
      }
    },
    [
      loading,
      loadingAttendances,
      filters.selectedGroupId,
      filters.selectedDate,
      savingIds,
      setSavingForId,
      updateAttendancesCache,
    ]
  );

  const loadAssiduity = useCallback(
    async (ids = []) => {
      const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
      const idsToLoad = uniqueIds.filter((id) => !assiduityInfo[id]);

      if (!idsToLoad.length || !filters.selectedGroupId) return;

      try {
        const results = await Promise.all(
          idsToLoad.map((id) =>
            countAttendance(id, filters.selectedGroupId)
              .then((data) => ({ id, data }))
              .catch(() => ({ id, data: null }))
          )
        );

        setAssiduityInfo((previous) => {
          const next = { ...previous };

          results.forEach(({ id, data }) => {
            if (data) next[id] = data;
          });

          return next;
        });
      } catch {
        // silencieux
      }
    },
    [assiduityInfo, filters.selectedGroupId]
  );

  const stats = useMemo(() => {
    let present = 0;
    let absent = 0;
    let neutral = 0;

    filteredAdherents.forEach((adherent) => {
      const value = attendance?.[adherent.id] ?? 0;

      if (value === 1) present += 1;
      else if (value === 2) absent += 1;
      else neutral += 1;
    });

    const total = filteredAdherents.length;
    const filled = present + absent;
    const completion = total > 0 ? Math.round((filled / total) * 100) : 0;

    return {
      total,
      present,
      absent,
      neutral,
      filled,
      completion,
    };
  }, [filteredAdherents, attendance]);

  const tableRows = useMemo(() => {
    if (!selectedMonth || !filters.selectedGroupId) return [];

    return buildAttendanceRows({
      month: selectedMonth,
      adherentsList: groupAdherents,
      attendancesList: allAttendances || [],
      groupId: filters.selectedGroupId,
    });
  }, [selectedMonth, filters.selectedGroupId, groupAdherents, allAttendances]);

  const tableKpis = useMemo(() => {
    const presents = tableRows.reduce(
      (sum, row) => sum + Number(row.totalPresent || 0),
      0
    );

    const absents = tableRows.reduce(
      (sum, row) => sum + Number(row.totalAbsent || 0),
      0
    );

    const totalMarked = presents + absents;
    const rate =
      totalMarked > 0 ? Math.round((presents / totalMarked) * 100) : 0;

    const bestPresent = tableRows.length
      ? Math.max(...tableRows.map((row) => Number(row.totalPresent || 0)))
      : 0;

    const bestCount =
      bestPresent > 0
        ? tableRows.filter(
            (row) => Number(row.totalPresent || 0) === bestPresent
          ).length
        : 0;

    return {
      presents,
      absents,
      totalMarked,
      rate,
      bestPresent,
      bestCount,
    };
  }, [tableRows]);

  const highlightIds = useMemo(() => {
    if (!tableRows.length) return [];

    const maxPresent = Math.max(
      ...tableRows.map((row) => Number(row.totalPresent || 0))
    );

    if (maxPresent <= 0) return [];

    return tableRows
      .filter((row) => Number(row.totalPresent || 0) === maxPresent)
      .map((row) => row.id);
  }, [tableRows]);

  const toggleTableCell = useCallback(
    async (adherentId, day, currentValue) => {
      if (!selectedMonth || !filters.selectedGroupId) return;

      const groupId = filters.selectedGroupId;
      const dateISO = `${selectedMonth}-${day}`;

      const nextState =
        currentValue === true
          ? "ABSENT"
          : currentValue === false
          ? "CLEAR"
          : "PRESENT";

      const previousAttendances = Array.isArray(allAttendances)
        ? allAttendances
        : [];

      const matchesCell = (attendance) =>
        Number(attendance.adherent_id) === Number(adherentId) &&
        Number(attendance.group_id) === Number(groupId) &&
        String(attendance.date_presence).slice(0, 10) === dateISO;

      let optimisticAttendances = [];

      if (nextState === "PRESENT") {
        optimisticAttendances = [
          ...previousAttendances.filter((attendance) => !matchesCell(attendance)),
          {
            adherent_id: Number(adherentId),
            group_id: Number(groupId),
            presence: true,
            date_presence: dateISO,
          },
        ];
      } else if (nextState === "ABSENT") {
        optimisticAttendances = [
          ...previousAttendances.filter((attendance) => !matchesCell(attendance)),
          {
            adherent_id: Number(adherentId),
            group_id: Number(groupId),
            presence: false,
            date_presence: dateISO,
          },
        ];
      } else {
        optimisticAttendances = previousAttendances.filter(
          (attendance) => !matchesCell(attendance)
        );
      }

      setAllAttendances(optimisticAttendances);

      try {
        if (nextState === "PRESENT") {
          await apiSetAttendance(adherentId, groupId, dateISO, true);
        } else if (nextState === "ABSENT") {
          await apiSetAttendance(adherentId, groupId, dateISO, false);
        } else {
          await apiClearAttendance(adherentId, groupId, dateISO);
        }

        if (String(filters.selectedDate).slice(0, 7) === selectedMonth) {
          loadGroupAttendance();
        }
      } catch (error) {
        console.error("Erreur mise à jour tableau :", error);
        toast.error("Échec de la mise à jour de la case.");
        setAllAttendances(previousAttendances);
      }
    },
    [
      selectedMonth,
      filters.selectedGroupId,
      filters.selectedDate,
      allAttendances,
      loadGroupAttendance,
    ]
  );

  const chartData = useMemo(() => {
    return buildMonthlyChartData({
      season: seasonActive,
      attendancesList: allAttendances || [],
      groupId: filters.selectedGroupId,
    });
  }, [seasonActive, allAttendances, filters.selectedGroupId]);

  const chartTotals = useMemo(() => getChartTotals(chartData), [chartData]);

  const refresh = useCallback(async () => {
    await loadBaseData();
    await loadGroupAttendance();

    try {
      setLoadingAttendances(true);

      const data = await getAllAttendances();
      setAllAttendances(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur actualisation appels :", error);
    } finally {
      setLoadingAttendances(false);
    }
  }, [loadBaseData, loadGroupAttendance]);

  const changeGroup = useCallback(() => {
    setFiltersState((previous) =>
      normalizeAttendanceFilters({
        ...previous,
        selectedGroupId: "",
        query: "",
        onlyPresent: false,
        onlyAbsent: false,
      })
    );

    setActivePanel("call");
    setAttendance({});
    setDailyAttendanceCache([]);
    attendanceRef.current = {};
  }, []);

  const setDeleteFormValue = useCallback((key, value) => {
    setDeleteForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  }, []);

  const deletePeriod = useCallback(async () => {
    if (!filters.selectedGroupId) {
      toast.error("Sélectionnez d’abord un groupe.");
      return;
    }

    if (!deleteForm.startDate || !deleteForm.endDate) {
      toast.error("Veuillez sélectionner une date de début et une date de fin.");
      return;
    }

    if (deleteForm.startDate > deleteForm.endDate) {
      toast.error("La date de début doit être avant la date de fin.");
      return;
    }

    const confirmDelete = window.confirm(
      `Supprimer les appels du groupe ${
        selectedGroup?.nom || ""
      } du ${deleteForm.startDate} au ${deleteForm.endDate} ?`
    );

    if (!confirmDelete) return;

    try {
      setLoadingAttendances(true);

      const result = await deleteAttendancesByPeriod({
        groupId: filters.selectedGroupId,
        startDate: deleteForm.startDate,
        endDate: deleteForm.endDate,
      });

      setDeleteResult(result);

      setAllAttendances((previous) => {
        if (!Array.isArray(previous)) return previous;

        return previous.filter((attendanceItem) => {
          const date = String(attendanceItem.date_presence).slice(0, 10);

          return !(
            Number(attendanceItem.group_id) === Number(filters.selectedGroupId) &&
            date >= deleteForm.startDate &&
            date <= deleteForm.endDate
          );
        });
      });

      await loadGroupAttendance();

      toast.success(`${result?.deleted || 0} appel(s) supprimé(s).`);
    } catch (error) {
      console.error("Erreur suppression période :", error);
      toast.error("Erreur lors de la suppression des appels.");
    } finally {
      setLoadingAttendances(false);
    }
  }, [
    filters.selectedGroupId,
    deleteForm,
    selectedGroup,
    loadGroupAttendance,
  ]);

  const months = useMemo(
    () => generateSeasonMonths(seasonActive),
    [seasonActive]
  );

  const actions = {
    refresh,
    changeGroup,
    toggleAttendance,
    loadAssiduity,
  };

  return {
    loading: loading || loadingAttendances,

    groups,
    selectedGroup,
    activePanel,
    setActivePanel,

    adherents,
    groupAdherents,
    filteredAdherents,
    seasons,

    filters,
    setFilter,

    attendance,
    dailyAttendanceCache,
    allAttendances,

    savingIds,
    assiduityInfo,
    statusOptions: STATUS_OPTIONS,

    stats,

    table: {
      months,
      selectedMonth,
      setSelectedMonth,
      rows: tableRows,
      kpis: tableKpis,
      highlightIds,
      toggleCell: toggleTableCell,
    },

    charts: {
      seasonActive,
      chartData,
      totals: chartTotals,
    },

    deletePanel: {
      form: deleteForm,
      setFormValue: setDeleteFormValue,
      deletePeriod,
      result: deleteResult,
    },

    actions,
  };
}